import { sumBy } from 'lodash';
import React from 'react';
import { GoInfo } from 'react-icons/go';

import CampaignStatus from '../campaign-editor/CampaignStatus';
import DaysFromToday from '../generic/DaysFromToday';
import IconLink from '../generic/IconLink';
import ListSelect from '../generic/ListSelect';
import StatusStackedBar from '../generic/StatusStackedBar/StatusStackedBar';
import { FILTER_TYPES } from '../generic/Table/utils';
import Tooltip from '../generic/Tooltip';
import { UserLinks } from '../users/Link';
import UserLink from '../users/UserLink';
import { withFullName } from '../users/utils';
import {
  boolColumn,
  currencyRangeColumn,
  dateRangeColumn,
  filterMultiBy,
  filterSingleBy,
  simpleColumn,
} from '../utils/column-helpers';
import {
  brandLinks as originalBrandLinks,
  ownerLink,
  quoteLink,
  salesTeamColumn,
  salesUnitColumn,
} from '../utils/columns';
import CampaignLink from './CampaignLink';
import HELP_TEXTS from './help-texts';
import { getStatusBarItems } from './utils/utils';

export const nCode = { ...simpleColumn('NCode', 'nCode', 90), sortKey: 'id' };

export const salesUnit = salesUnitColumn(
  'opportunity.salesUnit',
  'opportunity__salesUnit',
  'salesUnit',
);
export const salesTeam = salesTeamColumn(
  'opportunity.salesTeam',
  'opportunity__salesTeam',
  'salesTeam',
);

export const status = {
  Header: 'Status',
  id: 'status',
  accessor: 'status',
  width: 100,
  Cell: row => (
    <div className="d-flex align-items-center">
      <CampaignStatus status={row.value} small />
    </div>
  ),
};

export const setupStatus = {
  Header: 'Setup Status',
  id: 'setupStatus',
  filterable: false,
  width: 95,
  accessor: getStatusBarItems,
  Cell: row => (
    <div className="mt-2 mr-2 d-flex align-items-center">
      <StatusStackedBar items={row.value} key={row.original.id} />
    </div>
  ),
};

export const nameLink = {
  Header: 'Name',
  accessor: 'name',
  width: 200,
  Cell: row => <CampaignLink model={row.original} />,
};

export const brandLinks = {
  ...originalBrandLinks,
  width: 200,
};

export const netClientBudget = currencyRangeColumn({
  Header: 'Net Client Budget',
  accessor: 'netClientBudget',
  id: 'netClientBudget',
  width: 150,
});

export const coInvest = currencyRangeColumn({
  Header: 'CoInvest',
  accessor: 'coInvest',
  id: 'coInvest',
});

export const freeService = currencyRangeColumn({
  Header: 'Free Service',
  accessor: 'freeService',
  id: 'freeService',
});

export const plannedBudget = currencyRangeColumn({
  Header: 'Budget',
  accessor: 'spendableBudget',
  id: 'spendableBudget',
});

export const spentBudget = currencyRangeColumn({
  Header: 'Spent',
  accessor: 'totalSpentBudget',
  id: 'totalSpentBudget',
  width: 100,
});

export const openBudget = currencyRangeColumn({
  Header: 'Open Budget',
  accessor: 'openBudget',
  id: 'openBudget',
  width: 100,
  filterable: true,
  sortable: false,
});

export const offsiteSpent = currencyRangeColumn({
  Header: 'Offsite Spent',
  accessor: 'totalOffsiteSpentBudget',
  id: 'totalOffsiteSpentBudget',
  width: 100,
});

export const wkzBudget = currencyRangeColumn({
  Header: 'WKZ Budget',
  accessor: 'wkzBudget',
  id: 'wkzBudget',
  width: 100,
});

const plannedStart = dateRangeColumn(
  'Planned Start',
  'startDatePlan',
  105,
  true,
);
const plannedEnd = dateRangeColumn('Planned End', 'endDatePlan', 100, true);
const actualEnd = dateRangeColumn('Actual End', 'endDateIs', 100, true);

export const untilPlannedStart = {
  ...dateRangeColumn('Starts in', 'startDatePlan', 100, false),
  Cell: row => <DaysFromToday date={row.value} />,
};

export const untilPlannedEnd = {
  ...dateRangeColumn('Ends in', 'endDatePlan', 100, false),
  Cell: row => <DaysFromToday date={row.value} />,
};

const createInvoice = {
  Header: '',
  Label: 'Go to Invoicing',
  accessor: 'opportunity.id',
  filterable: false,
  sortable: false,
  Cell: row => (
    <IconLink
      to={`/opportunities/${row.value}/show?openBalanceModal=true`}
      showIcon
    >
      Go to Invoicing
    </IconLink>
  ),
};

const collaborators = {
  Header: 'Collaborators',
  accessor: 'collaborators',
  filterMethod: (filter, value) => {
    const users = value.collaborators.map(user => withFullName(user));
    return filterMultiBy(filter, users, 'fullName');
  },
  Cell: row => <UserLinks models={row.value} />,
  Filter: props => <ListSelect {...props} type="user" />,
  filterCriteria: FILTER_TYPES.LIST_SELECT,
  filterKey: 'opportunity__collaborators__id',
  sortable: false,
};

export const pendingChangeRequestsCount = {
  Header: () => (
    <>
      <span id="pending-change-requests-count-header">
        CRs
        <GoInfo className="infoTooltip ml-1" />
      </span>
      <Tooltip placement="top" target="pending-change-requests-count-header">
        {HELP_TEXTS.listHeaders.pendingChangeRequestsCount}
      </Tooltip>
    </>
  ),
  Label: 'CRs',
  accessor: 'pendingChangeRequestsCount',
  id: 'pendingChangeRequestsCount',
  width: 60,
  Cell: row => (row.value > 0 ? row.value : ''),
  filterable: false,
  Footer: ({ data }) => sumBy(data, 'pendingChangeRequestsCount') || 0,
};

const campaignOwnerLink = {
  ...ownerLink,
  sortKey: 'opportunity__owner__firstName',
};

const opportunityStage = {
  ...simpleColumn('Opportunity Stage', 'opportunity.stage', 150),
};

export const campaignObjective = simpleColumn(
  'Campaign Objective',
  'opportunity.campaignObjective',
  100,
);

export const flexibilityTerms = simpleColumn(
  'Flexibility Terms',
  'quote.flexibilityTerms',
  130,
);

export const accountSfId = {
  ...simpleColumn('Account ID', 'opportunity.accountSfId', 140),
  filterKey: 'accountSfId',
  filterable: true,
  sortable: false,
};

export const allowedFlexibility = {
  Header: 'Allowed Flexibility',
  accessor: 'quote.allowedFlexibilities',
  width: 200,
  Cell: row => row.value.join(', '),
  sortable: false,
};

export const commercialIncentive = simpleColumn(
  'Commercial Incentive',
  'commercialIncentive',
);

export const scalableBudget = boolColumn(
  'Scalable Budget',
  'hasScalableBudget',
);

export const opsOnsite = {
  Header: 'Ops Onsite',
  accessor: 'opsOnsite',
  width: 200,
  filterMethod: (filter, value) =>
    value.opsOnsite && filterSingleBy(filter, value.opsOnsite.id),
  Cell: row => row.value && <UserLink model={row.value} />,
  Filter: props => <ListSelect {...props} type="user" />,
  filterCriteria: FILTER_TYPES.LIST_SELECT,
  filterKey: 'opsOnsite__id',
  sortKey: 'opsOnsite__firstName',
};

export const opsCreative = {
  Header: 'Ops Creative',
  accessor: 'opsCreative',
  width: 200,
  filterMethod: (filter, value) =>
    value.opsCreative && filterSingleBy(filter, value.opsCreative.id),
  Cell: row => row.value && <UserLink model={row.value} />,
  Filter: props => <ListSelect {...props} type="user" />,
  filterCriteria: FILTER_TYPES.LIST_SELECT,
  filterKey: 'opsCreative__id',
  sortKey: 'opsCreative__firstName',
};

export const targetKpi = simpleColumn('Target KPI', 'targetKpi');

export const targetKpiValue = currencyRangeColumn({
  Header: 'Target KPI Value',
  accessor: 'targetKpiValue',
  id: 'targetKpiValue',
  width: 90,
  hasPrefix: false,
  hasFooter: false,
});

export const toBeInvoicedColumns = [
  accountSfId,
  nCode,
  nameLink,
  status,
  untilPlannedStart,
  untilPlannedEnd,
  plannedEnd,
  actualEnd,
  quoteLink,
  ownerLink,
  netClientBudget,
  coInvest,
  freeService,
  plannedBudget,
  createInvoice,
];

export const defaultListColumns = [
  accountSfId,
  nCode,
  nameLink,
  status,
  setupStatus,
  opportunityStage,
  netClientBudget,
  coInvest,
  freeService,
  plannedBudget,
  spentBudget,
  openBudget,
  offsiteSpent,
  pendingChangeRequestsCount,
  untilPlannedStart,
  untilPlannedEnd,
  plannedStart,
  plannedEnd,
  brandLinks,
  salesUnit,
  salesTeam,
  campaignOwnerLink,
  collaborators,
];

export const allListColumns = [
  accountSfId,
  nCode,
  nameLink,
  status,
  setupStatus,
  opportunityStage,
  netClientBudget,
  coInvest,
  freeService,
  plannedBudget,
  spentBudget,
  openBudget,
  offsiteSpent,
  pendingChangeRequestsCount,
  untilPlannedStart,
  untilPlannedEnd,
  plannedStart,
  plannedEnd,
  brandLinks,
  salesUnit,
  salesTeam,
  campaignOwnerLink,
  collaborators,
  wkzBudget,
  opsOnsite,
  opsCreative,
  commercialIncentive,
  scalableBudget,
  flexibilityTerms,
  allowedFlexibility,
  campaignObjective,
  targetKpi,
  targetKpiValue,
];
